import { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  where,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './useAuth';
import {
  Notification,
  markNotificationAsRead,
  formatNotificationTime,
  getUserNotifications,
  getUnreadNotificationCount
} from '../lib/notificationService';

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

/**
 * Real-time notifications hook with optimized performance
 */
export const useNotifications = (limitCount: number = 10): UseNotificationsReturn => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Keep track of unsubscribe functions
  const unsubscribeNotifications = useRef<Unsubscribe | null>(null);
  const unsubscribeUnreadCount = useRef<Unsubscribe | null>(null);

  /**
   * Mark a notification as read
   */
  const markAsRead = async (notificationId: string): Promise<void> => {
    if (!user) return;

    try {
      await markNotificationAsRead(user.uid, notificationId);
      // Real-time listener will automatically update the state
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  /**
   * Manually refresh notifications (fallback)
   */
  const refreshNotifications = async (): Promise<void> => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const [notificationsData, unreadCountData] = await Promise.all([
        getUserNotifications(user.uid, limitCount),
        getUnreadNotificationCount(user.uid)
      ]);

      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
    } catch (err) {
      console.error('Error refreshing notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Setup real-time listeners
   */
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Real-time notifications listener
      const notificationsRef = collection(db, `userNotifications/${user.uid}/notifications`);
      const notificationsQuery = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      unsubscribeNotifications.current = onSnapshot(
        notificationsQuery,
        (snapshot) => {
          const notificationsData: Notification[] = [];

          snapshot.forEach((doc) => {
            const data = doc.data();

            // Skip expired notifications
            if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
              return;
            }

            notificationsData.push({
              id: doc.id,
              ...data
            } as Notification);
          });

          setNotifications(notificationsData);
          setLoading(false);
        },
        (err) => {
          console.error('Error in notifications listener:', err);
          setError('Failed to load notifications');
          setLoading(false);
        }
      );

      // Real-time unread count listener
      const unreadQuery = query(
        notificationsRef,
        where('read', '==', false)
      );

      unsubscribeUnreadCount.current = onSnapshot(
        unreadQuery,
        (snapshot) => {
          // Filter out expired notifications
          const validNotifications = snapshot.docs.filter(doc => {
            const data = doc.data();
            return !data.expiresAt || data.expiresAt.toDate() > new Date();
          });

          setUnreadCount(validNotifications.length);
        },
        (err) => {
          console.error('Error in unread count listener:', err);
        }
      );

    } catch (err) {
      console.error('Error setting up notification listeners:', err);
      setError('Failed to setup real-time notifications');
      setLoading(false);
    }

    // Cleanup function
    return () => {
      if (unsubscribeNotifications.current) {
        unsubscribeNotifications.current();
        unsubscribeNotifications.current = null;
      }
      if (unsubscribeUnreadCount.current) {
        unsubscribeUnreadCount.current();
        unsubscribeUnreadCount.current = null;
      }
    };
  }, [user, limitCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    refreshNotifications
  };
};

/**
 * Hook for getting notification display helpers
 */
export const useNotificationHelpers = () => {
  return {
    formatTime: formatNotificationTime,
  };
};

export default useNotifications;